import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MagnifyingGlass,
  ClipboardText,
  Handshake,
  Wrench,
  ArrowRight,
  Phone,
  CheckCircle,
  Clock,
} from "@phosphor-icons/react";
import { COMPANY } from "../lib/constants";

const steps = [
  {
    number: "01",
    icon: MagnifyingGlass,
    title: "Analyse du logement",
    desc: "Nous échangeons avec vous pour comprendre la configuration de votre logement, votre chauffage actuel et vos objectifs en matière de confort et d'économies.",
    bullets: [
      "Discussion téléphonique ou en visio",
      "Compréhension de vos attentes",
      "Premier état des lieux technique",
    ],
  },
  {
    number: "02",
    icon: ClipboardText,
    title: "Étude d'éligibilité",
    desc: "Nous étudions les solutions techniques adaptées à votre situation et identifions les dispositifs d'aide potentiellement mobilisables selon votre profil.",
    bullets: [
      "Solutions dimensionnées pour votre logement",
      "Présentation des aides potentielles",
      "Estimation du reste à charge",
    ],
  },
  {
    number: "03",
    icon: Handshake,
    title: "Proposition adaptée",
    desc: "Vous recevez une proposition claire, détaillée et sans engagement. Nous prenons le temps de répondre à toutes vos questions avant que vous preniez votre décision.",
    bullets: [
      "Devis détaillé et transparent",
      "Explications claires, sans jargon",
      "Sans engagement",
    ],
  },
  {
    number: "04",
    icon: Wrench,
    title: "Installation par un professionnel",
    desc: "Un artisan qualifié réalise l'installation dans les règles de l'art, avec un suivi complet. Notre accompagnement continue jusqu'à la mise en service et au-delà.",
    bullets: [
      "Chantier réalisé par un professionnel qualifié",
      "Suivi de chantier",
      "Mise en service et accompagnement post-installation",
    ],
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-page">
      <section className="eco-gradient py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">
            Comment ça marche
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight max-w-3xl">
            Un parcours clair,{" "}
            <span className="bg-gradient-to-r from-blue-700 to-emerald-600 bg-clip-text text-transparent">
              en 4 étapes simples
            </span>
          </h1>
          <p className="text-slate-600 mt-5 text-base md:text-lg max-w-2xl leading-relaxed">
            Une méthode transparente pour avancer sur votre projet de rénovation
            énergétique, en toute sérénité.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`how-step-${i}`}
                className="relative grid md:grid-cols-[auto_1fr] gap-6 p-6 md:p-8 bg-white rounded-3xl border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
              >
                <div className="flex md:flex-col items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-700 to-emerald-500 flex items-center justify-center text-white">
                    <Icon size={28} weight="fill" />
                  </div>
                  <div className="font-heading text-4xl font-bold text-slate-200">
                    {s.number}
                  </div>
                </div>
                <div>
                  <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                    {s.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed mb-4">{s.desc}</p>
                  <ul className="grid md:grid-cols-3 gap-3">
                    {s.bullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-sm text-slate-700"
                      >
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className="text-emerald-600 flex-shrink-0 mt-0.5"
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">
            Commencez par l'étape 1 : on vous rappelle.
          </h2>
          <p className="text-blue-100 mb-7 max-w-xl mx-auto">
            Étude personnalisée et sans engagement. Aides disponibles selon
            votre situation, reste à charge variable selon profil.
          </p>
          <p className="text-sm text-emerald-200 font-medium mb-5 flex items-center gap-2 justify-center">
            <Clock size={14} weight="fill" />
            Réponse rapide sous 24h – étude gratuite et sans engagement
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/eligibilite"
              data-testid="how-eligibility-btn"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-semibold bg-emerald-500 hover:bg-emerald-600"
            >
              Tester mon éligibilité gratuitement <ArrowRight size={16} weight="bold" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="how-call-btn"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-xl font-semibold bg-white text-blue-800 hover:bg-slate-100"
            >
              <Phone size={16} weight="fill" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
